/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";
import debounce from "lodash/debounce";

import { Plate } from "src/classes/plate/Plate";

import { Session, SessionType } from "./session.types";
import { ArrowSession, DragBlockSession, DragCanvasSession, ResizeSession } from "./sessions";

type SessionManagerSnapshot = {
  isCurrentSession: boolean; // needed to observe for various ui elements
};

export class SessionManager extends Observable<SessionManagerSnapshot> {
  currentSession: Session | null = null;

  plate: Plate;

  private constructor(snapshot: SessionManagerSnapshot, plate: Plate) {
    super(snapshot);
    this.plate = plate;
  }

  /* -------------------------------------------------- */
  /*               Start / End / Cancel                 */
  /* -------------------------------------------------- */
  public startSession(type: SessionType): void {
    const session = this.createSession(type);
    this.currentSession = session;
    this.setState({ isCurrentSession: true });
    this.currentSession.start();
  }

  public endSession(): void {
    this.currentSession?.end();
    this.setState({ isCurrentSession: false });
    this.currentSession = null;
  }

  public debouncedEndSession = debounce(this.endSession, 1000);

  public cancelSession = (): void => {
    this.currentSession?.cancel();
    this.setState({ isCurrentSession: false });
    this.currentSession = null;
  };

  /* -------------------------------------------------- */
  /*               Session Instantiation                */
  /* -------------------------------------------------- */
  private createSession(type: SessionType): Session {
    switch (type) {
      case SessionType.Arrow:
        return ArrowSession.create(this.plate);
      case SessionType.DragBlock:
        return DragBlockSession.create(this.plate);
      case SessionType.DragCanvas:
        return DragCanvasSession.create(this.plate);
      case SessionType.Resize:
        return ResizeSession.create(this.plate);
    }
  }

  /* -------------------------------------------------- */
  /*                        Utils                       */
  /* -------------------------------------------------- */
  /**
   * Immediately invoke any functions that have pending writes to the stack.
   */
  public flushPendingStackWrites(): void {
    if (this.currentSession?.snapshot.type === SessionType.Arrow) {
      this.debouncedEndSession.flush();
    }
  }

  public static create(plate: Plate): SessionManager {
    const initialSnapshot = { isCurrentSession: false };
    return new SessionManager(initialSnapshot, plate);
  }
}
